import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import { Field, Label, ErrorMessage } from "../../components/ui/fieldset";
import { Heading } from "../../components/ui/heading";
import { Input } from "../../components/ui/input";
import { Text, TextLink } from "../../components/ui/text";
import { AuthLayout } from "../../components/ui/auth-layout";
import { useNavigate } from "react-router";
import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import { forgotPassword } from "../../api/AuthApi";

const DELIVERY_TYPES = {
  TEMPORARY_PASSWORD: "temporary_password",
  RESET_CODE: "reset_code",
};

const FormField = ({ label, ...props }) => {
  const [field, meta] = useField(props);
  const hasError = meta.touched && meta.error;

  return (
    <Field>
      <Label>{label}</Label>
      <Input
        {...field}
        {...props}
        className={hasError ? "border-red-500" : ""}
      />
      {hasError && <ErrorMessage>{meta.error}</ErrorMessage>}
    </Field>
  );
};

const getDefaultSuccessMessage = (deliveryType) => {
  if (deliveryType === DELIVERY_TYPES.TEMPORARY_PASSWORD) {
    return "We sent a temporary password to your email. Sign in with it, then you will be prompted to create a new password.";
  }

  return "We sent a 6-digit password reset code to your email. Use it on the next screen to set a new password.";
};

const ForgotPasswordWrapper = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [deliveryType, setDeliveryType] = useState(DELIVERY_TYPES.RESET_CODE);
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
  });

  const submitForgotPassword = async (email) => {
    setIsLoading(true);
    setError("");

    try {
      const response = await forgotPassword(email);
      const nextDeliveryType =
        response?.delivery_type || DELIVERY_TYPES.RESET_CODE;

      setUserEmail(email);
      setDeliveryType(nextDeliveryType);
      setSuccessMessage(
        response?.message || getDefaultSuccessMessage(nextDeliveryType)
      );
      setIsSubmitted(true);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to send password reset instructions. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    await submitForgotPassword(values.email);
    setSubmitting(false);
  };

  const handleResend = async () => {
    if (!userEmail) {
      setIsSubmitted(false);
      return;
    }

    await submitForgotPassword(userEmail);
  };

  if (isSubmitted) {
    const isTemporaryPassword =
      deliveryType === DELIVERY_TYPES.TEMPORARY_PASSWORD;

    return (
      <AuthLayout>
        <div className="flex flex-col space-y-6">
          <Heading>
            {isTemporaryPassword
              ? "Temporary Password Sent"
              : "Reset Code Sent"}
          </Heading>

          <Text>
            {successMessage} We sent it to{" "}
            <span className="font-medium">{userEmail}</span>.
          </Text>

          {isTemporaryPassword ? (
            <>
              <Text>
                Your account is in a force change password state. Use the
                temporary password from your email to sign in, then create a new
                password when prompted.
              </Text>
              <Button onClick={() => navigate("/login")} className="w-full">
                Back to Sign In
              </Button>
            </>
          ) : (
            <>
              <Text>
                Your account is active. Check your inbox for a 6-digit reset
                code, then enter it on the next screen along with your new
                password.
              </Text>
              <Button
                onClick={() =>
                  navigate(
                    `/reset-password?email=${encodeURIComponent(userEmail)}`
                  )
                }
                className="w-full"
              >
                Enter Reset Code
              </Button>
            </>
          )}

          <div className="text-center">
            <Text>
              Didn't receive the email?{" "}
              <TextLink onClick={handleResend} disabled={isLoading}>
                {isLoading
                  ? "Sending..."
                  : isTemporaryPassword
                    ? "Resend Temporary Password"
                    : "Resend Reset Code"}
              </TextLink>
            </Text>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="flex flex-col space-y-6">
        <Heading>Forgot Password</Heading>
        <Text>
          Enter your email address and we will send the correct instructions
          based on your account status. Accounts that must change their password
          on first sign-in receive a temporary password. Active accounts receive
          a reset code.
        </Text>

        {error && (
          <div className="text-red-500 text-sm text-center">{error}</div>
        )}

        <Formik
          initialValues={{ email: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-6">
              <FormField
                name="email"
                label="Email Address"
                type="email"
                autoComplete="email"
                required
              />

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || isSubmitting}
              >
                {isLoading ? "Sending..." : "Send Instructions"}
              </Button>

              <div className="text-center">
                <Text>
                  Remember your password?{" "}
                  <TextLink to="/login">Sign In</TextLink>
                </Text>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </AuthLayout>
  );
};

export default ForgotPasswordWrapper;
